import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAuth } from "../../api/auth/useAuth";
import { useHttp } from "../../api/http/useHttp";
import { components } from "../../api/generated-types";
import { useChatAccess } from "./useChatAccess";

export type ChatContract = components["schemas"]["AdminChatContract"];
export type ChatConversation = components["schemas"]["ChatConversation"];
export type ChatMessage = components["schemas"]["ChatMessage"];
type Contracts = components["schemas"]["AdminChatContractsResponse"];
type Conversations = components["schemas"]["AdminChatConversationsResponse"];
type Messages = components["schemas"]["ChatMessagesResponse"];
export type History = { messages: ChatMessage[]; hasOlder: boolean };

export function useViewerActivity() {
  const [active, setActive] = useState(() => !document.hidden && document.hasFocus());
  useEffect(() => {
    const update = () => setActive(!document.hidden && document.hasFocus());
    document.addEventListener("visibilitychange", update);
    window.addEventListener("focus", update);
    window.addEventListener("blur", update);
    return () => {
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("focus", update);
      window.removeEventListener("blur", update);
    };
  }, []);
  return active;
}

export const mergeMessages = (old: ChatMessage[], incoming: ChatMessage[]) =>
  [...new Map([...old, ...incoming].map((message) => [message.id, message])).values()].sort(
    (a, b) => a.sequence - b.sequence,
  );

const queryOptions = {
  placeholderData: undefined,
  staleTime: 0,
  gcTime: 0,
  refetchInterval: 15_000,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: "always" as const,
  refetchOnReconnect: "always" as const,
  retry: (count: number, error: AxiosError) =>
    ![401, 403, 404].includes(error.response?.status || 0) && count < 2,
};

export function useAdminChats(
  input: { page: number; search: string; unread_only: boolean },
  contract: string,
) {
  const { client } = useHttp();
  const { self } = useAuth();
  const enabled = useChatAccess();
  const cache = useQueryClient();
  const email = self?.email;
  useEffect(() => {
    if (!enabled) return;
    // React Query v5's default focus manager observes visibilitychange, not window.focus.
    const refresh = () => {
      if (!document.hidden) {
        void cache.refetchQueries(
          {
            queryKey: ["admin-chats", email],
            type: "active",
            predicate: (query) =>
              ["contracts", "conversations", "history"].includes(String(query.queryKey[2])),
          },
          { cancelRefetch: false },
        );
      }
    };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [cache, email, enabled]);
  const prefix = ["admin-chats", email];
  const contracts = useQuery<Contracts, AxiosError>({
    ...queryOptions,
    queryKey: [...prefix, "contracts", input],
    enabled,
    queryFn: async ({ signal }) =>
      (await client.post<Contracts>("/synota/chats/contracts", input, { signal })).data,
  });
  const conversations = useQuery<Conversations, AxiosError>({
    ...queryOptions,
    queryKey: [...prefix, "conversations", contract],
    enabled: enabled && Boolean(contract),
    queryFn: async ({ signal }) =>
      (
        await client.post<Conversations>(
          "/synota/chats/conversations",
          { contract_uuid: contract },
          { signal },
        )
      ).data,
  });
  return { contracts, conversations };
}

export function useAdminChatHistory(conversation: string) {
  const { client } = useHttp();
  const { self } = useAuth();
  const enabled = useChatAccess();
  const cache = useQueryClient();
  const prefix = ["admin-chats", self?.email];
  const queryKey = [...prefix, "history", conversation];
  const history = useQuery<History, AxiosError>({
    ...queryOptions,
    queryKey,
    enabled: enabled && Boolean(conversation),
    queryFn: async ({ signal }) => {
      const previous = cache.getQueryData<History>(queryKey);
      if (!previous?.messages.length) {
        const { data } = await client.post<Messages>(
          "/synota/chats/history",
          { conversation_id: conversation },
          { signal },
        );
        return { messages: data.messages, hasOlder: data.has_more };
      }
      // Read every catch-up page, not just the newest 50 messages.
      let messages = previous.messages;
      let hasMore = true;
      while (hasMore) {
        const after = messages[messages.length - 1].sequence;
        const { data } = await client.post<Messages>(
          "/synota/chats/history",
          { conversation_id: conversation, after },
          { signal },
        );
        messages = mergeMessages(messages, data.messages);
        hasMore = data.has_more && data.messages.length > 0;
      }
      // An older-history request can finish while catch-up is in flight.
      const current = cache.getQueryData<History>(queryKey) || previous;
      return { messages: mergeMessages(current.messages, messages), hasOlder: current.hasOlder };
    },
  });
  const older = useMutation({
    gcTime: 0,
    mutationFn: async () => {
      const current = cache.getQueryData<History>(queryKey);
      const { data } = await client.post<Messages>("/synota/chats/history", {
        conversation_id: conversation,
        before: current?.messages[0]?.sequence,
      });
      return data;
    },
    onSuccess: (data) =>
      cache.setQueryData<History>(queryKey, (current) => ({
        messages: mergeMessages(current?.messages || [], data.messages),
        hasOlder: data.has_more,
      })),
  });
  const read = useMutation({
    mutationFn: async (sequence: number) => {
      await client.post("/synota/chats/read", {
        conversation_id: conversation,
        read_sequence: sequence,
      });
      return sequence;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: [...prefix, "contracts"] });
      void cache.invalidateQueries({ queryKey: [...prefix, "conversations"] });
    },
  });
  return { history, older, read };
}
